/**
 * Utility functions for determining participant status based on scorecard state
 */

import type { Participant, Solution } from '@/types/evaluation.types';
import { storageService } from '@/services/local-storage';

/**
 * Possible participant statuses in the evaluation workflow
 */
export type ParticipantStatus =
  | 'Pending'
  | 'Declined'
  | 'Accepted'
  | 'Scoring'
  | 'Scorecard Submitted';

/**
 * Result of participant status check
 */
export interface ParticipantStatusInfo {
  /** Current status of the participant */
  status: ParticipantStatus;
  /** Link to the participant's scorecard (if exists) */
  scorecardLink: string | null;
  /** Whether the participant has created a scorecard */
  hasScorecard: boolean;
  /** Whether the scorecard has been submitted */
  isSubmitted: boolean;
}

/**
 * Determines the current status of a participant based on their invitation status
 * and scorecard progress.
 *
 * Status flow:
 * 1. Pending/Declined → Uses invitation status directly
 * 2. Accepted + No scorecard → "Accepted"
 * 3. Accepted + Scorecard not submitted → "Scoring"
 * 4. Accepted + Scorecard submitted → "Scorecard Submitted"
 * 5. If scorecard reopened after submission → Back to "Scoring"
 *
 * @param participant - The participant to check status for
 * @param evaluationId - The evaluation ID
 * @param solutions - List of solutions in the evaluation
 * @returns Status information including display status and scorecard link
 */
export function getParticipantScorecardStatus(
  participant: Participant,
  evaluationId: string,
  solutions: Solution[]
): ParticipantStatusInfo {
  // If invitation not accepted, use the invitation status directly
  if (participant.invitationStatus === 'Pending') {
    return {
      status: 'Pending',
      scorecardLink: null,
      hasScorecard: false,
      isSubmitted: false,
    };
  }

  if (participant.invitationStatus === 'Declined') {
    return {
      status: 'Declined',
      scorecardLink: null,
      hasScorecard: false,
      isSubmitted: false,
    };
  }

  // For accepted participants, check scorecard status for this specific participant
  // Find the first solution with a scorecard for this participant
  const solutionWithScorecard = solutions.find((solution) =>
    storageService.getScorecardBySolution(evaluationId, solution.id, participant.id) !== null
  );

  // No scorecard exists yet for this participant
  if (!solutionWithScorecard) {
    return {
      status: 'Accepted',
      scorecardLink: null,
      hasScorecard: false,
      isSubmitted: false,
    };
  }

  // Get the scorecard to check submission status
  const scorecard = storageService.getScorecardBySolution(
    evaluationId,
    solutionWithScorecard.id,
    participant.id
  );

  if (!scorecard) {
    // Should never happen due to check above, but be defensive
    return {
      status: 'Accepted',
      scorecardLink: null,
      hasScorecard: false,
      isSubmitted: false,
    };
  }

  // Scorecard exists - determine if it's submitted or in progress
  const scorecardLink = `/evaluation/${evaluationId}/scorecard/${solutionWithScorecard.id}/${participant.id}`;

  if (scorecard.isSubmitted) {
    return {
      status: 'Scorecard Submitted',
      scorecardLink,
      hasScorecard: true,
      isSubmitted: true,
    };
  }

  // Scorecard exists but not submitted (or was reopened)
  return {
    status: 'Scoring',
    scorecardLink,
    hasScorecard: true,
    isSubmitted: false,
  };
}
